/**
 * EQuIP Performance Query Hooks
 *
 * React Query hooks for provider performance and user context endpoints.
 * Wraps equipApi.performance.* and equipApi.context.* with typed key factories
 * and enabled guards for conditional parameters.
 */

import { useQuery } from '@tanstack/react-query'
import { equipApi } from '@/services/equipApi'
import type { ProviderPerformanceDetail, UserContext } from '@/lib/equip-types'

// ---------------------------------------------------------------------------
// Key factory
// ---------------------------------------------------------------------------

export const equipPerformanceKeys = {
  all: () => ['equip', 'performance'] as const,
  myPerformance: (year: number, quarter: number) =>
    ['equip', 'performance', 'me', year, quarter] as const,
  providerPerformance: (email: string, year: number, quarter: number) =>
    ['equip', 'performance', 'provider', email, year, quarter] as const,
  myContext: () => ['equip', 'context', 'me'] as const,
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Fetches the authenticated user's own performance data for a given year/quarter.
 * No enabled guard needed — year/quarter are always valid numbers.
 */
export function useMyPerformance(year: number, quarter: number) {
  return useQuery<ProviderPerformanceDetail>({
    queryKey: equipPerformanceKeys.myPerformance(year, quarter),
    queryFn: () => equipApi.performance.getMyPerformance({ year, quarter }),
  })
}

/**
 * Fetches a specific provider's performance data.
 * Query is disabled when email is null/undefined/empty — no request fires.
 */
export function useProviderPerformance(
  email: string | null | undefined,
  year: number,
  quarter: number
) {
  return useQuery<ProviderPerformanceDetail>({
    queryKey: equipPerformanceKeys.providerPerformance(email ?? '', year, quarter),
    queryFn: () =>
      equipApi.performance.getProviderPerformance(email!, { year, quarter }),
    enabled: !!email,
  })
}

/**
 * Fetches the authenticated user's context (ACO status, role, etc.).
 * Context rarely changes within a session — inherits 60-minute default staleTime.
 * The query can be disabled for users who do not have EQuIP access.
 */
export function useMyContext(enabled = true) {
  return useQuery<UserContext>({
    queryKey: equipPerformanceKeys.myContext(),
    queryFn: () => equipApi.context.getMyContext(),
    enabled,
  })
}
